import type { AppSearchJumpTarget } from './appSearchTypes';

/**
 * #1392 — jump helpers for the app-wide search surface.
 *
 * These are deliberately decoupled from the gutschrift-check canvas
 * internals: the in-app jump targets the React-Flow node by its
 * already-rendered ``data-id="de_<groupId>"`` attribute (MatchGraph
 * builds these ids today — see useMatchGroups/MatchGraph), so nothing
 * in the chokepoint files needs to change.
 */

/** Build the ``/documents?search=…`` fallback URL for a query. */
export function documentsFallbackUrl(query: string): string {
  const trimmed = query.trim();
  if (!trimmed) return '/documents';
  return `/documents?search=${encodeURIComponent(trimmed)}`;
}

/**
 * CSS selector for the gutschrift-check delivery node of a group.
 * MatchGraph renders the first delivery card as ``de_<group_id>``.
 */
export function gsNodeSelector(groupId: string): string {
  // Escape characters that would break the attribute-value string.
  const escaped = groupId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `.react-flow__node[data-id="de_${escaped}"]`;
}

/** Parse the ``translate(x, y)`` (px) out of a CSS transform string. */
export function parseTranslate(transform: string): { x: number; y: number } | null {
  const m = /translate\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*\)/.exec(transform);
  if (!m) return null;
  return { x: parseFloat(m[1]), y: parseFloat(m[2]) };
}

/** Parse the ``scale(k)`` out of a CSS transform string (default 1). */
export function parseScale(transform: string): number {
  const m = /scale\(\s*([\d.]+)\s*\)/.exec(transform);
  return m ? parseFloat(m[1]) : 1;
}

/**
 * The gutschrift-check EDITOR route — the one that renders the React-Flow
 * canvas (``GutschriftCheckPage``). ``/apps/gutschrift-check`` (no
 * ``/editor``) is the overview feed (``GutschriftCheckFeedPage``) and has
 * no ``.react-flow__node`` to jump to. See App.tsx route table.
 */
export const GS_EDITOR_ROUTE = '/apps/gutschrift-check/editor';

/** Resolve the route a jump target navigates to (for <a href> / navigate). */
export function resolveJumpHref(target: AppSearchJumpTarget): string {
  if (target.type === 'route') return target.to;
  return target.ym
    ? `${GS_EDITOR_ROUTE}?ym=${encodeURIComponent(target.ym)}`
    : GS_EDITOR_ROUTE;
}

/**
 * Pan the React-Flow canvas so the gutschrift-check node for ``groupId``
 * is centred in the viewport. React-Flow's visible area is driven by the
 * ``.react-flow__viewport`` transform (a ``translate(...) scale(...)``),
 * NOT by document scrolling — so this sets that transform directly rather
 * than calling ``scrollIntoView`` (which would not move an xyflow canvas).
 *
 * Returns true when the node + viewport were found and the pan was
 * applied; false when the node isn't mounted yet (caller retries or falls
 * through to the /documents fallback).
 */
export function scrollGsNodeIntoView(groupId: string): boolean {
  const node = document.querySelector<HTMLElement>(gsNodeSelector(groupId));
  if (!node) return false;

  const viewport = document.querySelector<HTMLElement>('.react-flow__viewport');
  const container =
    viewport?.closest<HTMLElement>('.react-flow') ??
    (node.closest<HTMLElement>('.react-flow') as HTMLElement | null);

  if (viewport && container) {
    const nodePos = parseTranslate(node.style.transform);
    if (nodePos) {
      const scale = parseScale(viewport.style.transform);
      const nodeW = node.offsetWidth || 0;
      const nodeH = node.offsetHeight || 0;
      const rect = container.getBoundingClientRect();
      // Centre the node centre (flow coords) in the container.
      const tx = rect.width / 2 - scale * (nodePos.x + nodeW / 2);
      const ty = rect.height / 2 - scale * (nodePos.y + nodeH / 2);
      viewport.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    } else {
      // No parsable node position — fall back to native scroll so the
      // node is at least brought into the document viewport.
      node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  } else {
    node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }

  // Transient highlight so the operator can spot the landed node. The
  // class is plain (page CSS owns the rule) — see AppSearchResults.css.
  node.classList.add('app-search-jump-target');
  window.setTimeout(() => node.classList.remove('app-search-jump-target'), 2000);
  return true;
}
